
import { useState } from 'react';
import {
  clientDocuments,
  documentCategories,
} from '../../../mocks/clientPortalData';

export default function DocumentsSection() {
  const [activeCategory, setActiveCategory] = useState('All Documents');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredDocs = clientDocuments.filter((doc) => {
    const matchesCategory =
      activeCategory === 'All Documents' || doc.category === activeCategory;
    const matchesSearch = doc.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const statusColor = (status: string) => {
    switch (status) {
      case 'Ready':
        return 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20';
      case 'Processing':
        return 'bg-amber-500/15 text-amber-400 border border-amber-500/20';
      case 'Pending Review':
        return 'bg-sky-500/15 text-sky-400 border border-sky-500/20';
      default:
        return 'bg-white/10 text-white/60';
    }
  };

  return (
    <section>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2
            className="text-2xl font-bold text-[#0a1628]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Your Documents
          </h2>
          <p className="text-sm text-[#5a6a7e] mt-1">
            {clientDocuments.length} documents available
          </p>
        </div>
        <div className="relative">
          <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-[#5a6a7e]">
            <i className="ri-search-line text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-md text-sm text-[#0a1628] placeholder-[#5a6a7e]/60 focus:outline-none focus:border-[#00e5c7] transition-colors w-full md:w-72"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {documentCategories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeCategory === cat
                ? 'bg-[#0a1628] text-[#00e5c7]'
                : 'bg-gray-100 text-[#5a6a7e] hover:bg-gray-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-[#5a6a7e] font-semibold">
          <div className="col-span-5">Document</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-1">Size</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Action</div>
        </div>

        {filteredDocs.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-lg">
              <i className="ri-file-search-line text-2xl text-[#5a6a7e]"></i>
            </div>
            <p className="text-sm text-[#5a6a7e]">No documents found</p>
          </div>
        ) : (
          filteredDocs.map((doc, idx) => (
            <div
              key={doc.id}
              className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 items-center hover:bg-gray-50/80 transition-colors ${
                idx < filteredDocs.length - 1
                  ? 'border-b border-gray-100'
                  : ''
              }`}
            >
              <div className="md:col-span-5 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0a1628]/5 rounded-lg flex-shrink-0">
                  <i className={`${doc.icon} text-lg text-[#0a1628]`}></i>
                </div>
                <span className="text-sm font-medium text-[#0a1628] truncate">
                  {doc.name}
                </span>
              </div>
              <div className="md:col-span-2 text-xs text-[#5a6a7e]">
                {doc.category}
              </div>
              <div className="md:col-span-2 text-xs text-[#5a6a7e]">
                {new Date(doc.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
              <div className="md:col-span-1 text-xs text-[#5a6a7e]">
                {doc.size}
              </div>
              <div className="md:col-span-1">
                <span
                  className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${statusColor(
                    doc.status
                  )}`}
                >
                  {doc.status}
                </span>
              </div>
              <div className="md:col-span-1 flex justify-end gap-2">
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#00e5c7]/10 text-[#5a6a7e] hover:text-[#00e5c7] transition-colors cursor-pointer"
                  title="Download"
                >
                  <i className="ri-download-line text-base"></i>
                </button>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#0a1628]/5 text-[#5a6a7e] hover:text-[#0a1628] transition-colors cursor-pointer"
                  title="Preview"
                >
                  <i className="ri-eye-line text-base"></i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
